import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  RotateCw,
  ZoomIn,
  ZoomOut,
  ExternalLink,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  Search,
  Wand2,
} from "lucide-react";
import { api } from "@/lib/api";
import { getStoredToken, getStoredOutlet } from "@/lib/authStorage";
import { TID } from "@/constants/testIds";
import StatusChip, { OriginChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { formatAmount, asText } from "@/lib/format";
import { useSession } from "@/contexts/SessionContext";
import { useVendorCategories } from "@/hooks/useVendorCategories";
import { useBillsAuthority } from "@/hooks/useBillsAuthority";
import UnitCombobox from "@/components/UnitCombobox";
import { deriveLineItem, computeBillFooter, printedTaxTotal, summarizeCharges, totalsMismatch, round2 } from "@/lib/lineItemDerive";

// Operator-facing names for the canonical printed components. Only rows the bill prints are ever shown.
const COMPONENT_LABEL = {
  GST: "GST", CGST: "CGST", SGST: "SGST", UGST: "UGST", IGST: "IGST", GST_CESS: "Cess",
  VAT: "VAT", TCS: "TCS", DELIVERY_FREIGHT: "Delivery / freight", PACKING: "Packing",
  SERVICE_CHARGE: "Service charge", OTHER_CHARGE: "Other charge", DISCOUNT: "Discount",
  ROUND_OFF: "Round off", UNCLASSIFIED: "Unclassified charge",
};
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2 as Loading } from "lucide-react";

const VENDOR_RESOLVED = "RESOLVED_EXISTING";

function BannerForStatus({ candidate }) {
  let tone = "info";
  let text = "Reviewing candidate…";
  switch (candidate.status) {
    case "READY_TO_SEND":
    case "READY_TO_CONFIRM":  // legacy
      tone = "success";
      text = "Ready to confirm. Review the details, then continue in 1OS Bills.";
      break;
    case "NEEDS_REVIEW":
      tone = "warn";
      text = "Review required. Some fields need confirmation.";
      break;
    case "BLOCKED":
      tone = "error";
      text = "Blocked. Resolve issues before sending.";
      break;
    case "OCR_FAILED":
      tone = "error";
      text = candidate.error_message
        ? `OCR failed: ${asText(candidate.error_message)}`
        : "OCR failed. Retry OCR or enter the bill manually.";
      break;
    case "OCR_PROCESSING":
      tone = "info";
      text = "OCR is in progress. This usually takes a few seconds.";
      break;
    case "SENDING_TO_EC":
    case "CONFIRMING":  // legacy
      tone = "info";
      text = "Legacy Expense Capture handoff in progress (historical).";
      break;
    case "CONFIRMED_IN_BILLS":
      tone = "success";
      text = `Confirmed in 1OS Bills. Canonical bill ${(candidate.confirmed_bill_id || "").slice(0, 8)}.`;
      break;
    case "SENT_TO_EC":
    case "CONFIRMED_LINKED":  // legacy
      tone = "success";
      text = candidate.ec_bill_url
        ? `Historical: handed to Expense Capture before 1OS Bills became canonical.`
        : `Historical: handed to Expense Capture before 1OS Bills became canonical. Record ${(candidate.ec_draft_bill_id || candidate.confirmed_bill_id || "").slice(0, 8)}.`;
      break;
    case "SEND_FAILED":
      tone = "error";
      text = candidate.error_message
        ? `Legacy Expense Capture handoff failed (historical): ${asText(candidate.error_message)}`
        : "Legacy Expense Capture handoff failed (historical). Continue this bill in 1OS Bills.";
      break;
    case "REJECTED":
      tone = "neutral";
      text = "This candidate was rejected.";
      break;
    default:
      break;
  }
  const cls = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    error: "bg-red-50 border-red-200 text-red-800",
    neutral: "bg-archival-soft border-archival text-archival-secondary",
  }[tone];
  return (
    <div
      data-testid={TID.reviewStatusBanner}
      className={`w-full py-2 px-4 text-[13px] font-medium border-b flex items-center gap-2 ${cls}`}
    >
      {tone === "success" ? <CheckCircle2 size={14} /> : tone === "error" ? <XCircle size={14} /> : <AlertTriangle size={14} />}
      <span>{text}</span>
      <span className="ml-auto text-[11px] opacity-80 font-mono uppercase tracking-wider">
        CANDIDATE · NOT A BILL
      </span>
    </div>
  );
}

function VendorChangePicker({ candidate, onChange, trigger }) {
  // Shared "Change Vendor" popover used by RESOLVED_EXISTING /
  // SUGGESTED_MATCH / AMBIGUOUS_MATCH branches. Two tabs — Search
  // existing vendors, or Create new vendor. The Create-new tab reuses
  // the same <NewVendorForm /> that already ships in the unresolved
  // branch, so the persistence contract (`selected_new_vendor` →
  // `is_new_vendor: true` at Send to EC) is unchanged.
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // "search" | "new"
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (search.trim().length === 0) { setResults([]); return; }
      try {
        const r = await api.searchVendors(search);
        if (!cancelled) setResults(r);
      } catch (_e) {/* ignore */}
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const pickExisting = async (vendor) => {
    try {
      await api.updateCandidate(candidate.id, {
        selected_vendor_id: vendor.id,
        selected_vendor_name: vendor.name,
      });
      toast.success(`Vendor set to ${vendor.name}`);
      setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                  || e?.response?.data?.detail || "Could not set vendor");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-[420px] p-0 bg-white"
        data-testid="vendor-change-picker"
        align="end"
      >
        {/* Tabs */}
        <div className="flex border-b border-archival">
          <button
            type="button"
            data-testid="vendor-picker-tab-search"
            onClick={() => setMode("search")}
            className={`flex-1 px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
              mode === "search"
                ? "border-archival-primary text-archival-primary"
                : "border-transparent text-archival-muted hover:text-archival-primary"
            }`}
          >
            <Search size={12} className="inline mr-1.5" /> Search existing
          </button>
          <button
            type="button"
            data-testid="vendor-picker-tab-new"
            onClick={() => setMode("new")}
            className={`flex-1 px-3 py-2 text-[12px] font-medium border-b-2 transition-colors ${
              mode === "new"
                ? "border-archival-primary text-archival-primary"
                : "border-transparent text-archival-muted hover:text-archival-primary"
            }`}
          >
            + Create new vendor
          </button>
        </div>
        <div className="p-2">
          {mode === "search" ? (
            <>
              <Input
                placeholder="Search vendors…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2 border-archival"
                data-testid="vendor-picker-search-input"
                autoFocus
              />
              <div className="max-h-60 overflow-y-auto">
                {results.map((vv) => (
                  <button
                    key={vv.id}
                    data-testid={TID.reviewVendorSelect(vv.id)}
                    onClick={() => pickExisting(vv)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-archival-soft text-[13px]"
                  >
                    <div className="font-medium text-archival-primary">{vv.name}</div>
                    {vv.gst && <div className="text-[11px] text-archival-muted font-mono">{vv.gst}</div>}
                  </button>
                ))}
                {search && results.length === 0 && (
                  <div className="text-[12px] text-archival-muted px-2 py-1.5">
                    No matches. Switch to <button
                      type="button"
                      onClick={() => setMode("new")}
                      className="underline text-archival-primary font-medium"
                    >Create new vendor</button> instead.
                  </div>
                )}
              </div>
            </>
          ) : (
            <NewVendorForm
              candidate={candidate}
              onChange={() => { setOpen(false); onChange?.(); }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function VendorPanel({ candidate, onChange }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (search.trim().length === 0) {
        setResults([]);
        return;
      }
      try {
        const r = await api.searchVendors(search);
        if (!cancelled) setResults(r);
      } catch (_e) {
        /* ignore search errors */
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  const v = candidate;
  const selectedVendorId = v.selected_vendor_id;
  const state = v.vendor_resolution_state;

  const setExistingVendor = async (vendor, { acknowledged = false } = {}) => {
    try {
      await api.updateCandidate(v.id, {
        selected_vendor_id: vendor.id,
        selected_vendor_name: vendor.name,
        ...(acknowledged ? { identity_acknowledged: true } : {}),
      });
      toast.success(`Vendor set to ${vendor.name}`);
      onChange?.();
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error_code === "vendor_identity_conflict"
          || d?.error_code === "vendor_identity_belongs_to_another_vendor") {
        toast.error(d.message, {
          action: {
            label: d.error_code === "vendor_identity_conflict"
              ? "Same vendor — continue"
              : "Separate registration — continue",
            onClick: () => setExistingVendor(vendor, { acknowledged: true }),
          },
          duration: 12000,
        });
        return;
      }
      toast.error((d && typeof d === "object" ? d.message : d) || "Could not set vendor");
    }
  };

  return (
    <section
      data-testid={TID.reviewVendorPanel}
      className="border border-archival rounded-md bg-white"
    >
      <header className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
        <div className="text-[13px] font-semibold text-archival-primary">Vendor</div>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-archival-muted">
          {state.replaceAll("_", " ")}
        </span>
      </header>
      <div className="p-4 space-y-3">
        <div className="text-[12px] text-archival-muted">
          OCR extracted: <span className="text-archival-primary font-mono">{v.extracted_vendor_name || "—"}</span>
        </div>

        <GovernmentIdentity candidate={v} onChange={onChange} />

        {state === VENDOR_RESOLVED && v.suggested_vendor_name && (
          <div className="flex items-center justify-between rounded-md bg-archival-soft border border-archival px-3 py-2">
            <div>
              <div className="text-[14px] font-semibold text-archival-primary">
                {v.suggested_vendor_name}
              </div>
              <div className="text-[11px] text-archival-muted">
                Matched existing vendor
                {v.vendor_match_reason ? ` · ${v.vendor_match_reason}` : ""}
                {typeof v.vendor_resolution_confidence === "number"
                  ? ` · ${Math.round(v.vendor_resolution_confidence * 100)}% confidence`
                  : ""}
              </div>
            </div>
            <VendorChangePicker
              candidate={v}
              onChange={onChange}
              trigger={
                <Button variant="outline" size="sm" data-testid={TID.reviewVendorSearch} className="border-archival bg-white">
                  Change Vendor
                </Button>
              }
            />
          </div>
        )}

        {state === "SUGGESTED_MATCH" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center justify-between">
              <div className="text-[13px] text-amber-900 font-medium">Suggested vendor</div>
              {(v.vendor_match_reason || typeof v.vendor_resolution_confidence === "number") && (
                <div className="text-[11px] text-amber-800">
                  {v.vendor_match_reason}
                  {typeof v.vendor_resolution_confidence === "number"
                    ? ` · ${Math.round(v.vendor_resolution_confidence * 100)}%`
                    : ""}
                </div>
              )}
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div>
                <div className="text-[14px] font-semibold text-archival-primary">{v.suggested_vendor_name}</div>
                {(() => {
                  const opt = (v.candidate_vendor_options || []).find(o => o.id === v.suggested_vendor_id);
                  const meta = [opt?.gst, opt?.category].filter(Boolean).join(" · ");
                  return meta ? <div className="text-[11px] text-archival-muted font-mono">{meta}</div> : null;
                })()}
              </div>
              <Button
                size="sm"
                data-testid={TID.reviewVendorAccept}
                onClick={() => setExistingVendor({ id: v.suggested_vendor_id, name: v.suggested_vendor_name })}
                className="text-white"
                style={{ backgroundColor: "var(--action)" }}
              >
                Accept Match
              </Button>
            </div>
            <div className="mt-2 pt-2 border-t border-amber-200 flex items-center justify-between text-[11px]">
              <span className="text-amber-800">Not the right one?</span>
              <VendorChangePicker
                candidate={v}
                onChange={onChange}
                trigger={
                  <button
                    type="button"
                    data-testid="vendor-change-from-suggested"
                    className="underline font-medium text-amber-900 hover:text-archival-primary"
                  >
                    Change vendor
                  </button>
                }
              />
            </div>
          </div>
        )}

        {state === "AMBIGUOUS_MATCH" && (
          <div className="space-y-2">
            <div className="text-[12px] text-archival-muted">Multiple vendors match — select one:</div>
            {(v.candidate_vendor_options || []).map((opt) => (
              <button
                key={opt.id}
                data-testid={TID.reviewVendorSelect(opt.id)}
                onClick={() => setExistingVendor(opt)}
                className="w-full text-left px-3 py-2 border border-archival rounded-md bg-white hover:bg-archival-soft transition-colors"
              >
                <div className="text-[14px] font-semibold text-archival-primary">{opt.name}</div>
                {(opt.gst || opt.category) && (
                  <div className="text-[11px] text-archival-muted font-mono">
                    {[opt.gst, opt.category].filter(Boolean).join(" · ")}
                  </div>
                )}
              </button>
            ))}
            <div className="flex items-center justify-between pt-1 text-[11px] text-archival-muted">
              <span>None of these?</span>
              <VendorChangePicker
                candidate={v}
                onChange={onChange}
                trigger={
                  <button
                    type="button"
                    data-testid="vendor-change-from-ambiguous"
                    className="underline font-medium text-archival-primary"
                  >
                    Search other / Create new
                  </button>
                }
              />
            </div>
          </div>
        )}

        {state === "CONFLICT_MATCH" && (
          <div
            data-testid="vendor-identity-conflict-state"
            className="rounded-md border border-red-300 bg-red-50 p-3 space-y-2"
          >
            <div className="text-[13px] font-semibold text-red-900">Vendor identity conflict</div>
            <div className="text-[12px] text-red-800">
              A government identifier on this bill contradicts an existing vendor. Correct the identity
              above, pick the right registration, or create the vendor this registration belongs to.
            </div>
            {(v.candidate_vendor_options || []).map((opt) => (
              <div
                key={opt.id}
                className="flex items-center justify-between rounded border border-red-200 bg-white px-3 py-2"
              >
                <div>
                  <div className="text-[13px] font-semibold text-archival-primary">{opt.name}</div>
                  <div className="text-[11px] text-archival-muted font-mono">
                    {[opt.gst, opt.pan, opt.vat_tin, opt.tax_jurisdiction].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={TID.reviewVendorSelect(opt.id)}
                  onClick={() => setExistingVendor(opt)}
                  className="border-archival bg-white"
                >
                  Use this vendor
                </Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1 text-[11px] text-red-800">
              <span>Different registration?</span>
              <VendorChangePicker
                candidate={v}
                onChange={onChange}
                trigger={
                  <button
                    type="button"
                    data-testid="vendor-change-from-conflict"
                    className="underline font-medium text-red-900"
                  >
                    Search other / Create new
                  </button>
                }
              />
            </div>
          </div>
        )}

        {state === "VENDOR_LOOKUP_FAILED" && (
          <div
            data-testid="vendor-lookup-failed"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900"
          >
            <div className="font-semibold">Vendor lookup unavailable</div>
            <div className="text-[12px] mt-0.5">
              {v.vendor_lookup_error
                ? `Reason: ${v.vendor_lookup_error}. `
                : ""}
              Search again or create a new vendor below — you can still confirm the bill.
            </div>
          </div>
        )}

        {(state === "NEW_VENDOR_REQUIRED" || state === "UNRESOLVED" ||
          state === "NEW_VENDOR_APPROVED" || state === "VENDOR_LOOKUP_FAILED" ||
          state === "CONFLICT_MATCH") && (
          <div className="rounded-md border border-archival bg-archival-soft p-3 space-y-3">
            <div className="flex items-start justify-between">
              <div className="text-[13px] text-archival-secondary">
                {state === "NEW_VENDOR_APPROVED"
                  ? "New vendor will be created on confirm:"
                  : state === "CONFLICT_MATCH"
                  ? "If this bill belongs to a registration you do not have yet, create it here."
                  : "No matching vendor found. Search existing or create a new one."}
              </div>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={TID.reviewVendorSearch}
                    className="border-archival bg-white"
                  >
                    <Search size={12} /> Search existing
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2 bg-white">
                  <Input
                    placeholder="Search vendors…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="mb-2 border-archival"
                  />
                  <div className="max-h-60 overflow-y-auto">
                    {results.map((vv) => (
                      <button
                        key={vv.id}
                        data-testid={TID.reviewVendorSelect(vv.id)}
                        onClick={() => setExistingVendor(vv)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-archival-soft text-[13px]"
                      >
                        <div className="font-medium text-archival-primary">{vv.name}</div>
                        {vv.gst && (
                          <div className="text-[11px] text-archival-muted font-mono">{vv.gst}</div>
                        )}
                      </button>
                    ))}
                    {search && results.length === 0 && (
                      <div className="text-[12px] text-archival-muted px-2 py-1.5">No matches</div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <NewVendorForm candidate={v} onChange={onChange} />
          </div>
        )}
      </div>
    </section>
  );
}

function GovernmentIdentity({ candidate, onChange }) {
  const ident = candidate.vendor_identity || {};
  const [gstin, setGstin] = useState(candidate.extracted_vendor_gst || "");
  const [pan, setPan] = useState(candidate.extracted_vendor_pan || "");
  const [vatTin, setVatTin] = useState(candidate.extracted_vendor_vat_tin || "");
  const [juris, setJuris] = useState(candidate.extracted_vendor_tax_jurisdiction || "");
  const [saving, setSaving] = useState(false);

  const dirty =
    (pan || "") !== (candidate.extracted_vendor_pan || "") ||
    (vatTin || "") !== (candidate.extracted_vendor_vat_tin || "") ||
    (juris || "") !== (candidate.extracted_vendor_tax_jurisdiction || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.updateCandidate(candidate.id, {
        extracted_vendor_pan: pan.trim().toUpperCase(),
        extracted_vendor_vat_tin: vatTin.trim().toUpperCase(),
        extracted_vendor_tax_jurisdiction: juris.trim(),
      });
      toast.success("Government identity saved");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not save vendor identity");
    } finally {
      setSaving(false);
    }
  };

  const STRENGTH_LABEL = {
    GSTIN: "GSTIN verified",
    PAN_VAT_TIN_JURISDICTION: "PAN + VAT/TIN + state",
    PAN_VAT_TIN_NO_JURISDICTION: "PAN + VAT/TIN, state missing",
    PAN_ONLY: "PAN only",
    VAT_TIN_ONLY: "VAT/TIN only",
    NONE: "No government identity",
  };
  const sufficient = ident.government_identity_sufficient;

  return (
    <div data-testid={TID.reviewVendorIdentityPanel} className="rounded-md border border-archival bg-archival-soft p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-archival-primary font-semibold">Government identity</div>
        <span
          data-testid={TID.reviewVendorIdentityStrength}
          className={`text-[11px] font-semibold uppercase tracking-wider ${
            (ident.acknowledged_conflicts || []).length ? "text-amber-700"
              : ident.identity_match === "EXACT" && sufficient ? "text-emerald-700"
              : ident.identity_match === "CONFLICT" ? "text-red-700" : "text-amber-700"}`}
        >
          {STRENGTH_LABEL[ident.identity_strength] || "Not checked"}
          {ident.identity_match ? ` · ${ident.identity_match.replaceAll("_", " ")}` : ""}
        </span>
      </div>

      {(ident.acknowledged_conflicts || []).length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[12px] text-amber-900">
          Identity difference accepted by an operator ·{" "}
          <span className="font-mono">{ident.acknowledged_conflicts.join(" · ")}</span>
        </div>
      )}

      {(ident.conflicts || []).length > 0 && (
        <div data-testid={TID.reviewVendorIdentityConflict} className="rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[12px] text-red-800">
          Vendor identity conflict — a printed identifier contradicts an existing vendor.{" "}
          <span className="font-mono">{ident.conflicts.join(" · ")}</span>
        </div>
      )}

      {!candidate.extracted_vendor_gst && (
        <div className="text-[11px] text-archival-primary/70">
          No GSTIN printed on this bill. PAN + VAT/TIN + state is then this supplier's government identity.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-archival-primary/70">GSTIN</Label>
          <Input
            data-testid={TID.reviewVendorIdentityGstin}
            value={gstin}
            onChange={(e) => setGstin(e.target.value)}
            disabled
            placeholder="Not printed"
            className="bg-white border-archival mt-1 font-mono text-[12px]"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-archival-primary/70">PAN</Label>
          <Input
            data-testid={TID.reviewVendorIdentityPan}
            value={pan}
            onChange={(e) => setPan(e.target.value)}
            placeholder="ABCDE1234F"
            className="bg-white border-archival mt-1 font-mono text-[12px]"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-archival-primary/70">VAT / TIN</Label>
          <Input
            data-testid={TID.reviewVendorIdentityVatTin}
            value={vatTin}
            onChange={(e) => setVatTin(e.target.value)}
            placeholder="State VAT / TIN"
            className="bg-white border-archival mt-1 font-mono text-[12px]"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-archival-primary/70">State</Label>
          <Input
            data-testid={TID.reviewVendorIdentityJurisdiction}
            value={juris}
            onChange={(e) => setJuris(e.target.value)}
            placeholder="Goa"
            className="bg-white border-archival mt-1 text-[12px]"
          />
        </div>
      </div>

      {dirty && (
        <div className="flex justify-end">
          <Button
            size="sm"
            data-testid={TID.reviewVendorIdentitySave}
            disabled={saving}
            onClick={save}
            className="text-white"
            style={{ backgroundColor: "var(--action)" }}
          >
            {saving ? "Saving…" : "Save identity"}
          </Button>
        </div>
      )}
    </div>
  );
}

function NewVendorForm({ candidate, onChange }) {
  const initial = candidate.selected_new_vendor || {};
  const inConflict = candidate.vendor_resolution_state === "CONFLICT_MATCH"
    || Boolean((candidate.vendor_identity || {}).conflicts?.length)
    || Boolean((candidate.vendor_identity || {}).acknowledged_conflicts?.length);
  const { currentOutletId } = useSession();
  const { categories, loading: catsLoading, error: catsError, ready: catsReady } = useVendorCategories();

  const [name, setName] = useState(initial.name || candidate.extracted_vendor_name || "");
  const [categoryId, setCategoryId] = useState(initial.category_id || "");
  const [categoryName, setCategoryName] = useState(initial.category_name || "");
  const [gst, setGst] = useState(initial.gst || candidate.extracted_vendor_gst || "");
  const [pan, setPan] = useState(initial.pan || candidate.extracted_vendor_pan || "");
  const [vatTin, setVatTin] = useState(initial.vat_tin || candidate.extracted_vendor_vat_tin || "");
  const [jurisdiction, setJurisdiction] = useState(
    initial.tax_jurisdiction || candidate.extracted_vendor_tax_jurisdiction || "");
  const [separateRegistration, setSeparateRegistration] = useState(
    Boolean(initial.identity_acknowledged));
  const [address, setAddress] = useState(initial.address || "");
  const [saving, setSaving] = useState(false);
  const [openCat, setOpenCat] = useState(false);

  // Outlet flip: hard-clear selected category so we can't carry an
  // outlet-A category over to outlet B. Hook clears its cache on the
  // session side; this clears the form-local state.
  const prevOutletRef = useRef(currentOutletId);
  useEffect(() => {
    if (prevOutletRef.current && prevOutletRef.current !== currentOutletId) {
      setCategoryId("");
      setCategoryName("");
    }
    prevOutletRef.current = currentOutletId;
  }, [currentOutletId]);

  // Verify selected category still belongs to this outlet's list.
  // If the operator picked a category and then the outlet changed mid-flight
  // and the hook returned a different list — drop the selection.
  useEffect(() => {
    if (!catsReady || !categoryId) return;
    const found = categories.find((c) => c.id === categoryId);
    if (!found) {
      setCategoryId("");
      setCategoryName("");
    } else if (found.name !== categoryName) {
      setCategoryName(found.name);
    }
  }, [catsReady, categories, categoryId, categoryName]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    if (catsReady && !catsError && !categoryId) {
      toast.error("Vendor category is required. Please select a category before continuing.");
      return;
    }
    setSaving(true);
    try {
      await api.updateCandidate(candidate.id, {
        selected_new_vendor: {
          name: name.trim(),
          category_id: categoryId || null,
          category_name: categoryName || null,
          gst,
          pan: pan.trim().toUpperCase() || null,
          vat_tin: vatTin.trim().toUpperCase() || null,
          tax_jurisdiction: jurisdiction.trim() || null,
          identity_acknowledged: separateRegistration,
          address,
        },
      });
      toast.success("New vendor approved — will be created on confirm");
      onChange?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object") ? d.message : (d || "Could not approve vendor"));
    } finally {
      setSaving(false);
    }
  };

  const activeCats = categories.filter((c) => c.active !== false);
  const showRequired = catsReady && !catsError;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Name *</Label>
        <Input
          data-testid={TID.reviewVendorCreateName}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white border-archival mt-1"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">
          Category {showRequired && <span className="text-red-600">*</span>}
        </Label>
        {!currentOutletId ? (
          <div
            data-testid="cat-need-outlet"
            className="mt-1 px-3 py-2 text-[12px] text-archival-muted border border-archival rounded-md bg-archival-soft"
          >
            Select outlet before choosing vendor category.
          </div>
        ) : catsError ? (
          <div
            data-testid="cat-error"
            className="mt-1 px-3 py-2 text-[12px] text-amber-900 border border-amber-200 rounded-md bg-amber-50"
          >
            {catsError.message}
          </div>
        ) : (
          <Popover open={openCat} onOpenChange={setOpenCat}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={TID.reviewVendorCreateCategory}
                disabled={catsLoading}
                className="mt-1 w-full inline-flex items-center justify-between gap-2 rounded-md border border-archival bg-white px-3 py-2 text-[13px] hover:bg-archival-soft disabled:opacity-50"
              >
                <span className={categoryId ? "text-archival-primary" : "text-archival-muted"}>
                  {catsLoading
                    ? "Loading categories…"
                    : categoryName || "Search category…"}
                </span>
                {catsLoading
                  ? <Loading size={14} className="animate-spin" />
                  : <ChevronDown size={14} className="text-archival-muted" />}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search category…" data-testid="cat-search-input" />
                <CommandList>
                  <CommandEmpty>
                    {activeCats.length === 0 ? "No categories available for this outlet." : "No matches."}
                  </CommandEmpty>
                  <CommandGroup>
                    {activeCats.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.name}
                        data-testid={`cat-option-${c.id}`}
                        onSelect={() => {
                          setCategoryId(c.id);
                          setCategoryName(c.name);
                          setOpenCat(false);
                        }}
                      >
                        {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">GST</Label>
        <Input
          data-testid={TID.reviewVendorCreateGst}
          value={gst}
          onChange={(e) => setGst(e.target.value)}
          className="bg-white border-archival mt-1 font-mono"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">PAN</Label>
        <Input
          data-testid={TID.reviewVendorCreatePan}
          value={pan}
          onChange={(e) => setPan(e.target.value)}
          placeholder="ABCDE1234F"
          className="bg-white border-archival mt-1 font-mono"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">VAT / TIN</Label>
        <Input
          data-testid={TID.reviewVendorCreateVatTin}
          value={vatTin}
          onChange={(e) => setVatTin(e.target.value)}
          className="bg-white border-archival mt-1 font-mono"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">VAT / TIN state</Label>
        <Input
          data-testid={TID.reviewVendorCreateJurisdiction}
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder="Goa"
          className="bg-white border-archival mt-1"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Address</Label>
        <Input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="bg-white border-archival mt-1"
        />
      </div>
      <div className="md:col-span-2 flex items-center justify-between gap-3">
        {inConflict ? (
          <label className="flex items-center gap-2 text-[11px] text-archival-secondary">
            <input
              type="checkbox"
              data-testid="review-vendor-create-separate-registration"
              checked={separateRegistration}
              onChange={(e) => setSeparateRegistration(e.target.checked)}
              className="accent-[var(--action)]"
            />
            This is a separate registration of a PAN we already have
          </label>
        ) : <span />}
        <Button
          size="sm"
          data-testid={TID.reviewVendorCreateSubmit}
          onClick={submit}
          disabled={saving || !name.trim()}
          className="text-white"
          style={{ backgroundColor: "var(--action)" }}
        >
          {saving ? <Loader2 className="animate-spin mr-1" size={12} /> : null}
          Approve New Vendor
        </Button>
      </div>
    </div>
  );
}

export default function CandidateReview() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const nativeAuthority = useBillsAuthority(candidateId);
  const billsOwned = nativeAuthority === "BILLS_NATIVE";
  const [searchParams] = useSearchParams();
  // Queue context — carried in URL by CandidateQueue.jsx so refresh
  // survives it. When present, Send to EC / Reject auto-advance to
  // the next candidate in the same filter instead of navigating to
  // /sent-to-ec.
  const queueStatus = searchParams.get("queue_status") || "";
  const queueBatchId = searchParams.get("batch_id") || "";
  const queueSearchQ = searchParams.get("q") || "";
  const hasQueueContext = !!queueStatus;
  const queueHref = () => {
    const qs = new URLSearchParams();
    if (queueStatus) qs.set("tab", queueStatus);
    if (queueBatchId) qs.set("batch_id", queueBatchId);
    if (queueSearchQ) qs.set("q", queueSearchQ);
    const s = qs.toString();
    return `/queue${s ? `?${s}` : ""}`;
  };
  const nextCandidateHref = (id) => {
    const qs = new URLSearchParams();
    if (queueStatus) qs.set("queue_status", queueStatus);
    if (queueBatchId) qs.set("batch_id", queueBatchId);
    if (queueSearchQ) qs.set("q", queueSearchQ);
    const s = qs.toString();
    return `/candidates/${id}${s ? `?${s}` : ""}`;
  };
  // Operator preference — persisted across sessions. Default ON.
  const [autoAdvance, setAutoAdvance] = useState(() => {
    try {
      const v = localStorage.getItem("bulk_capture_auto_advance");
      return v === null ? true : v === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try {
      localStorage.setItem("bulk_capture_auto_advance", String(autoAdvance));
    } catch { /* localStorage unavailable — non-fatal */ }
  }, [autoAdvance]);
  // Remaining count in the current queue — populated after a
  // successful Send/Reject to power the header progress chip on the
  // next candidate. Also refreshed once on mount if queue context
  // exists so operators see the queue depth before they act.
  const [queueRemaining, setQueueRemaining] = useState(null);
  useEffect(() => {
    if (!hasQueueContext || !candidateId) { setQueueRemaining(null); return; }
    let alive = true;
    api.getNextInQueue(candidateId, {
      status: queueStatus, batch_id: queueBatchId || undefined,
      q: queueSearchQ || undefined,
    })
      .then((r) => { if (alive) setQueueRemaining(r?.remaining_count ?? 0); })
      .catch(() => { if (alive) setQueueRemaining(null); });
    return () => { alive = false; };
  }, [candidateId, hasQueueContext, queueStatus, queueBatchId, queueSearchQ]);

  const [candidate, setCandidate] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [viewMode, setViewMode] = useState("fit"); // 'fit' | 'read'
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [extractingMissing, setExtractingMissing] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const objectUrlRef = useRef(null);
  // Facts about the DOCUMENT, immune to derivation: the bill-level tax it prints, and whether the operator
  // has touched any line since load (which makes the live totals verdict authoritative over the stored one).
  const docTaxTotalRef = useRef(null);
  // The PRINTED bill total as the document stated it — the reference that proves whether the printed line
  // amounts are tax-inclusive. Captured once on load, never overwritten by a recompute.
  const docTotalRef = useRef(null);
  const linesDirtyRef = useRef(false);
  // Grouped multi-image bill viewer — which page (by attachment_id) is
  // currently loaded in the main previewer. `null` for legacy
  // single-attachment candidates (fetches the top-level attachment).
  // Reset on candidate switch.
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  // Natural (intrinsic) dimensions of the currently-loaded image, read
  // once per image via <img onLoad>. Used to build the rotated bounding
  // box wrapper so the pane's scroll extent always contains all four
  // edges regardless of rotation.
  const [imgNat, setImgNat] = useState({ w: 0, h: 0 });
  const paneRef = useRef(null);
  const [paneSize, setPaneSize] = useState({ w: 0, h: 0 });
  // Observe pane size — needed to compute fit-scale (zoom=1 fits the
  // image inside the pane; zoom>1 scales up from that fit baseline).
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const update = () => setPaneSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paneRef.current]);
  useEffect(() => {
    // On candidate load / switch, default to page 1 for grouped bills.
    const atts = candidate?.attachments || [];
    if (atts.length >= 2) {
      const first = [...atts].sort((a, b) => (a.page_number || 0) - (b.page_number || 0))[0];
      setSelectedAttachmentId(first?.attachment_id || null);
    } else {
      setSelectedAttachmentId(null);
    }
    // Reset natural dims so onLoad re-populates them for the new image.
    setImgNat({ w: 0, h: 0 });
  }, [candidate?.id]);
  const orderedPages = useMemo(() => {
    return [...(candidate?.attachments || [])].sort(
      (a, b) => (a.page_number || 0) - (b.page_number || 0)
    );
  }, [candidate?.attachments]);
  const currentPageIdx = useMemo(() => {
    if (!selectedAttachmentId || orderedPages.length < 2) return -1;
    return orderedPages.findIndex((a) => a.attachment_id === selectedAttachmentId);
  }, [orderedPages, selectedAttachmentId]);
  const goToPage = (idx) => {
    if (idx < 0 || idx >= orderedPages.length) return;
    setSelectedAttachmentId(orderedPages[idx].attachment_id);
    setZoom(1);
    setRotation(0);
  };
  // EC Units — fetched once per mount. Backend caches 5 min per (org,
  // outlet). On failure we render free-text fallback inside the
  // Combobox, so candidate review is never blocked on units.
  const [units, setUnits] = useState([]);
  const [recentUnits, setRecentUnits] = useState([]);
  const [unitsDegraded, setUnitsDegraded] = useState(false);
  useEffect(() => {
    let alive = true;
    api.listUnits()
      .then((r) => {
        if (!alive) return;
        setUnits(r?.units || []);
        setRecentUnits(r?.recent_units || []);
        setUnitsDegraded(!!r?.degraded);
      })
      .catch(() => {
        // Network failure → treat as degraded; keeps the review screen
        // alive with a plain text input.
        if (alive) { setUnits([]); setRecentUnits([]); setUnitsDegraded(true); }
      });
    return () => { alive = false; };
  }, []);

  // Re-evaluate the scroll hint whenever the viewer mode flips (operator
  // may toggle to Read after the image already finished loading).
  useEffect(() => {
    if (viewMode !== "read") { setShowScrollHint(false); return; }
    const img = document.querySelector('[data-testid="viewer-img"]');
    const pane = document.querySelector('[data-testid="viewer-pane"]');
    if (!img || !pane) return;
    const overflows =
      img.getBoundingClientRect().height >
      pane.getBoundingClientRect().height + 4;
    setShowScrollHint(overflows);
  }, [viewMode, imgUrl, zoom, rotation]);

  const { has } = useSession();
  const canConfirm = has("BULK_CAPTURE_CONFIRM");
  const reload = async () => {
    try {
      const c = await api.getCandidate(candidateId);
      // Immutable reference to the tax the DOCUMENT states at bill level. Derived per-line tax may later
      // replace candidate.tax_total, so the footer must never read the document's figure from there.
      docTaxTotalRef.current = isFiniteNumeric(c?.tax_total)
        ? Number(c.tax_total)
        : printedTaxTotal(c?.printed_charges);
      docTotalRef.current = isFiniteNumeric(c?.total_amount) ? Number(c.total_amount) : null;
      linesDirtyRef.current = false;
      setCandidate(c);
    } catch (e) {
      toast.error("Failed to load candidate");
    }
  };

  useEffect(() => {
    reload();
    // poll while OCR_PROCESSING
    const id = setInterval(async () => {
      if (candidate && (candidate.status === "OCR_PROCESSING" || candidate.status === "UPLOADED")) {
        await reload();
      }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, candidate?.status]);

  // Fetch attachment as blob to avoid header issues.
  // Re-runs when the operator switches to a different page of a
  // grouped multi-image candidate (selectedAttachmentId changes).
  useEffect(() => {
    let abort = new AbortController();
    (async () => {
      if (!candidate) return;
      try {
        const token = getStoredToken();
        const outlet = getStoredOutlet();
        const reqHeaders = {};
        if (token) reqHeaders.Authorization = `Bearer ${token}`;
        if (outlet) reqHeaders["X-Selected-Outlet-Id"] = outlet;
        const url = selectedAttachmentId
          ? `${api.attachmentUrl(candidate.id)}?attachment_id=${encodeURIComponent(selectedAttachmentId)}`
          : api.attachmentUrl(candidate.id);
        const res = await fetch(url, { headers: reqHeaders, signal: abort.signal });
        if (!res.ok) return;
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = objUrl;
        setImgUrl(objUrl);
      } catch (_e) {
        /* attachment fetch may fail before candidate finishes loading */
      }
    })();
    return () => {
      abort.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [candidate?.id, selectedAttachmentId]);

  const update = async (patch) => {
    if (!candidate) return;
    setCandidate({ ...candidate, ...patch });
  };

  // One shared footer recompute for every line edit. Rules:
  //   • subtotal / tax_total follow the lines unless the operator EDITED them.
  //   • when no line carries a tax rate, the bill-level tax the document states is kept, never zeroed.
  //   • the PRINTED bill total is never overwritten — it is the reference the mismatch check compares
  //     against, so it is only filled in when the document gave us none.
  const footerPatch = (items) => {
    const billTax = docTaxTotalRef.current;
    const footer = computeBillFooter(items, {
      discount:  Number(candidate.discount_amount) || 0,
      round_off: Number(candidate.round_off) || 0,
      bill_tax_total: billTax,
      printed_total: docTotalRef.current,
      charges: summarizeCharges(candidate.printed_charges),
    });
    const patch = { line_items: items };
    if (candidate.subtotal_origin  !== "EDITED") { patch.subtotal  = footer.subtotal;  patch.subtotal_origin  = "DERIVED"; }
    if (candidate.tax_total_origin !== "EDITED") { patch.tax_total = footer.tax_total; patch.tax_total_origin = "DERIVED"; }
    if (candidate.total_amount_origin !== "EDITED" && !isFiniteNumeric(candidate.total_amount)) {
      patch.total_amount = footer.total_amount;
      patch.total_amount_origin = "DERIVED";
    }
    return patch;
  };


  // Single choke-point for line-item edits. Applies:
  //   1. the operator's edit    (field ← value + <field>_origin = EDITED)
  //   2. derivation rules       (fills the dependent numeric field, marked DERIVED)
  //   3. bill footer recompute  (subtotal / tax_total / total_amount)
  //
  // Values arrive as either a number, an empty string ("" from a
  // cleared input), or null. We normalize to `null` for empty so the
  // downstream save + derivation logic can treat "unset" uniformly.
  const updateLineItem = (index, field, rawValue) => {
    if (!candidate) return;
    let value = rawValue;
    if (typeof rawValue === "string") {
      if (rawValue === "") value = null;
      else {
        const n = Number(rawValue);
        value = Number.isNaN(n) ? null : n;
      }
    }
    const items = [...(candidate.line_items || [])];
    if (!items[index]) return;
    const prev = items[index];
    const prevQty = Number(prev.quantity);
    const prevLineTotal = Number(prev.line_total);
    const prevUnitPrice = Number(prev.unit_price);
    // A printed rate that is INCLUSIVE of tax makes qty × rate differ from the printed amount
    // (e.g. 70 × 154 = 10,780 against a printed 10,266.90). On a quantity change we then scale the
    // printed amount instead of recomputing it from the rate, so the document's value is never lost.
    const inclusiveRate = Number.isFinite(prevLineTotal) && Number.isFinite(prevQty) && prevQty !== 0
      && Number.isFinite(prevUnitPrice) && Math.abs(prevLineTotal - prevQty * prevUnitPrice) > 0.01;
    // Mark the edited field first — this is what lets `deriveLineItem`
    // know NOT to overwrite it during the derivation pass.
    items[index] = {
      ...items[index],
      [field]: value,
      [`${field}_origin`]: "EDITED",
    };
    items[index] = deriveLineItem(items[index], field);
    if (field === "quantity" && inclusiveRate && prev.line_total_origin !== "EDITED"
        && isFiniteNumeric(value) && Number(value) !== 0) {
      items[index] = {
        ...items[index],
        line_total: round2(prevLineTotal * (Number(value) / prevQty)),
        line_total_origin: "DERIVED",
      };
    }
    linesDirtyRef.current = true;

    // Bill-footer live recompute — but only for the *derived* totals.
    // We never touch total_amount / subtotal / tax_total that the
    // operator has explicitly EDITED (see origin lattice), and we never
    // overwrite the PRINTED bill total that OCR read off the document:
    // that figure is the reference the mismatch check compares against.
    const patch = footerPatch(items);
    setCandidate({ ...candidate, ...patch });
  };

  // Break the origin-lock on a manually-edited line_total and force a
  // fresh recompute from the current quantity × unit_price. Used by the
  // "Line total was manually edited · Recompute" affordance in the line
  // items table (rendered only when line_total_origin === "EDITED").
  //
  // Contract:
  //   • line_total ← round2(quantity × unit_price)
  //   • line_total_origin ← "DERIVED"
  //   • Preserves unit_price precision (no truncation of the multiplier)
  //   • Rounds the final currency result HALF-UP to 2 decimals (via round2)
  //   • Recomputes footer for any non-EDITED bill totals
  //
  // No-op if quantity or unit_price is not a present, finite number.
  // NOTE: `Number(null)` and `Number("")` both equal 0 (finite), so we
  // must explicitly reject null / undefined / "" before falling through
  // to Number.isFinite — otherwise Recompute would silently set
  // line_total to 0 when either field is blank.
  const isFiniteNumeric = (v) =>
    v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

  const recomputeLineTotal = (index) => {
    if (!candidate) return;
    const items = [...(candidate.line_items || [])];
    if (!items[index]) return;
    if (!isFiniteNumeric(items[index].quantity)) return;
    if (!isFiniteNumeric(items[index].unit_price)) return;
    const qty = Number(items[index].quantity);
    const up = Number(items[index].unit_price);
    items[index] = {
      ...items[index],
      line_total: round2(qty * up),
      line_total_origin: "DERIVED",
    };
    const patch = footerPatch(items);
    setCandidate({ ...candidate, ...patch });
  };

  // Single serializer used by `save` and the Bills continuation — must be kept
  // in sync with the backend `UpdateCandidateRequest` model. Extract
  // to avoid the origin-forwarding asymmetry that caused iter_12's
  // Send-to-EC bug (backend clobbered operator-edited footer values).
  const serializeCandidateForSave = () => ({
    bill_number: candidate.bill_number,
    bill_date: candidate.bill_date,
    currency: candidate.currency,
    subtotal: candidate.subtotal,
    tax_total: candidate.tax_total,
    total_amount: candidate.total_amount,
    subtotal_origin: candidate.subtotal_origin,
    tax_total_origin: candidate.tax_total_origin,
    total_amount_origin: candidate.total_amount_origin,
    printed_charges: candidate.printed_charges,
    line_items: candidate.line_items,
  });

  const save = async () => {
    setSaving(true);
    try {
      const out = await api.updateCandidate(candidate.id, serializeCandidateForSave());
      setCandidate(out);
      toast.success("Candidate saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };


  // Live footer preview — always computed from the current line-items,
  // used for the OCR-vs-computed mismatch banner and the Send-to-EC
  // guard hint. Wrapped in useMemo so we don't burn CPU on every render.
  // The printed non-item rows (VAT / TCS / freight / round off ...), classified by their printed label.
  const charges = useMemo(
    () => summarizeCharges(candidate?.printed_charges),
    [candidate?.printed_charges],
  );
  const computedFooter = useMemo(
    () => computeBillFooter(candidate?.line_items || [], {
      discount:  Number(candidate?.discount_amount) || 0,
      round_off: Number(candidate?.round_off) || 0,
      bill_tax_total: docTaxTotalRef.current,
      printed_total: docTotalRef.current,
      charges,
    }),
    [candidate?.line_items, candidate?.discount_amount, candidate?.round_off, candidate?.tax_total, charges],
  );

  // Operator correction of a mis-read printed amount: the printed LABEL stays authority for the type and
  // the original OCR amount is kept on the row, so the change is visible and auditable.
  const updateCharge = (index, raw) => {
    const rows = (candidate?.printed_charges || []).map((c, i) => {
      if (i !== index) return c;
      return {
        ...c,
        amount: raw === "" ? null : Number(raw),
        amount_origin: "EDITED",
        ocr_amount: c.ocr_amount != null ? c.ocr_amount : c.amount,
      };
    });
    update({ printed_charges: rows });
  };
  const totalMismatch = totalsMismatch(candidate?.total_amount, computedFooter.total_amount);


  // ----- Extract Missing Details (Bulk Capture-owned OCR rescue) -----
  // Re-runs OCR on the candidate's existing image and merges newly-found
  // numeric line-item fields into the candidate. fill_blank_only mode
  // never overwrites operator edits.
  const extractMissing = async () => {
    setExtractingMissing(true);
    try {
      const r = await api.extractMissingDetails(candidate.id, "fill_blank_only");
      await reload();
      const status = r.ocr_review_status;
      if (status === "COMPLETE") {
        toast.success(r.message || "Missing bill details extracted.");
      } else if (status === "PARTIAL") {
        toast.success(r.message || "Some details extracted — review remaining blanks.");
      } else {
        toast.error(r.message || "Could not detect a bill table.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object")
        ? detail.message
        : (typeof detail === "string" ? detail : e.message || "Extraction failed");
      toast.error(msg);
    } finally {
      setExtractingMissing(false);
    }
  };


  // Shared post-action auto-advance. Called after a confirmed 200
  // success from Send to EC / Reject when queue context is present
  // and the toggle is on. Falls back to /sent-to-ec (Send) or /queue
  // (Reject) when the queue is drained or context is missing.
  const advanceOrFallback = async (fallbackPath) => {
    if (!autoAdvance || !hasQueueContext) {
      navigate(fallbackPath);
      return;
    }
    try {
      const r = await api.getNextInQueue(candidate.id, {
        status: queueStatus, batch_id: queueBatchId || undefined,
        q: queueSearchQ || undefined,
      });
      if (r?.candidate_id) {
        setQueueRemaining(r.remaining_count);
        navigate(nextCandidateHref(r.candidate_id));
        return;
      }
      toast.success(`Queue cleared — ${queueStatus.replace(/_/g, " ").toLowerCase()} is empty`);
      navigate(queueHref());
    } catch {
      // If next-in-queue fails, fall through to the safe fallback so
      // the operator is never stranded on a read-only candidate.
      navigate(fallbackPath);
    }
  };

  const reject = async () => {
    try {
      await api.rejectCandidate(candidate.id, rejectReason);
      toast.success("Candidate rejected");
      setRejectOpen(false);
      await reload();
      // Auto-advance after successful reject too — operator throughput
      // matters the same way. Fallback to queue on drained/no-context.
      setTimeout(() => advanceOrFallback(queueHref()), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  const retryOcr = async () => {
    try {
      await api.retryOcr(candidate.id);
      toast.info("OCR re-queued");
      setTimeout(reload, 1500);
    } catch (e) {
      toast.error("Retry failed");
    }
  };

  const confirmDisabledReason = useMemo(() => {
    if (!candidate) return "";
    if (candidate.status === "CONFIRMED_IN_BILLS") return "Already confirmed in 1OS Bills";
    if (candidate.status === "SENT_TO_EC" || candidate.status === "CONFIRMED_LINKED")
      return "Already sent to EC";
    if (candidate.status === "REJECTED") return "Candidate rejected";
    if (candidate.status === "SENDING_TO_EC" || candidate.status === "CONFIRMING") return "Sending to EC…";
    if (candidate.attachment_state === "MISSING") return "Re-upload bill image before sending";
    if (candidate.duplicate_state === "HARD_DUPLICATE") return "Hard duplicate detected";
    if (candidate.status === "BLOCKED") return candidate.blocker_reasons?.[0] || "Resolve blockers before sending";
    if (!candidate.bill_number?.trim()) return "Bill number is required";
    if (!candidate.bill_date?.trim()) return "Bill date is required";
    if (candidate.total_amount === null || candidate.total_amount === undefined || candidate.total_amount === "")
      return "Total amount is required";
    if (!candidate.line_items?.length) return "At least one line item is required";
    if (
      candidate.vendor_resolution_state !== "RESOLVED_EXISTING" &&
      candidate.vendor_resolution_state !== "NEW_VENDOR_APPROVED"
    )
      return "Resolve vendor before sending";
    return "";
  }, [candidate]);

  if (!candidate) {
    return <div className="p-6 text-archival-secondary">Loading candidate…</div>;
  }

  const isReadOnly =
    candidate.status === "SENT_TO_EC" ||
    candidate.status === "CONFIRMED_LINKED" ||
    candidate.status === "CONFIRMED_IN_BILLS" ||
    candidate.status === "REJECTED";

  // Banner visibility for "Extract Missing Details": line items exist
  // AND at least one row is missing numeric details. Hidden in read-only.
  const needsExtractMissing = (() => {
    if (isReadOnly) return false;
    const items = candidate.line_items || [];
    if (items.length === 0) return false;
    const blank = (v) =>
      v === null || v === undefined || v === "" || Number(v) === 0;
    return items.some(
      (li) => blank(li.quantity) || blank(li.unit_price) || blank(li.line_total)
    );
  })();

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col bg-archival-base">
      <BannerForStatus candidate={candidate} />
      {billsOwned && (
        <div data-testid="bills-owned-banner" className="px-4 py-3 bg-[#eef3ee] border-b border-[#cfdccf] flex flex-wrap items-center justify-between gap-2">
          <span className="text-[14px] text-archival-primary">This bill is being reviewed in Bills.</span>
          <Button data-testid="open-bill-review-banner-btn" onClick={() => navigate(`/bills/${candidateId}`)}
            className="h-11 text-white" style={{ backgroundColor: "var(--action)" }}>Open bill review</Button>
        </div>
      )}

      <div className="px-6 py-3 border-b border-archival bg-white flex items-center justify-between">
        <button
          onClick={() => navigate(hasQueueContext ? queueHref() : -1)}
          className="inline-flex items-center gap-1.5 text-[13px] text-archival-secondary hover:text-archival-primary"
          data-testid="review-back-to-queue"
        >
          <ArrowLeft size={14} /> {hasQueueContext ? "Back to queue" : "Back"}
        </button>
        <div className="flex items-center gap-3">
          {hasQueueContext && (
            <span
              data-testid="review-queue-progress"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-wider bg-archival-soft border border-archival text-archival-secondary"
              title={`Auto-advance queue · ${queueStatus.replace(/_/g, " ")}`}
            >
              <span className="text-archival-primary">{queueStatus.replace(/_/g, " ")}</span>
              <span className="text-archival-muted">·</span>
              <span>
                {queueRemaining === null
                  ? "…"
                  : queueRemaining === 0
                  ? "last bill"
                  : `${queueRemaining} remaining`}
              </span>
            </span>
          )}
          <label
            data-testid="review-auto-advance-toggle"
            className="inline-flex items-center gap-1.5 text-[11px] text-archival-secondary cursor-pointer select-none"
            title="Open next bill in the queue after you finish this one"
          >
            <input
              type="checkbox"
              className="h-3 w-3 accent-archival-primary"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
              data-testid="review-auto-advance-input"
            />
            Continue to next bill
          </label>
          <div className="text-[12px] text-archival-muted font-mono">
            CANDIDATE · {candidate.id.slice(0, 8)}
          </div>
          {candidate.grouping_mode === "MANUAL" &&
            !["SENT_TO_EC", "CONFIRMED_LINKED", "SENDING_TO_EC", "CONFIRMING"].includes(candidate.status) && (
              <Button
                variant="outline"
                size="sm"
                data-testid="review-ungroup-btn"
                className="h-7 text-[11px] border-archival text-archival-secondary hover:text-archival-primary"
                onClick={async () => {
                  if (!window.confirm(
                    `This will restore the ${candidate.page_count || candidate.attachments?.length || 2} source files as separate candidates.`
                  )) return;
                  try {
                    const r = await api.ungroupCandidate(candidate.id);
                    toast.success(`Ungrouped — ${r.restored_count} source files restored`);
                    navigate(hasQueueContext ? queueHref() : "/queue");
                  } catch (e) {
                    const detail = e?.response?.data?.detail;
                    const msg = (detail && typeof detail === "object") ? detail.message : (detail || "Ungroup failed");
                    toast.error(msg);
                  }
                }}
              >
                Ungroup
              </Button>
            )}
          <StatusChip status={candidate.status} />
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 overflow-hidden">
        {/* Attachment preview */}
        <div
          data-testid={TID.reviewAttachment}
          className="bg-archival-soft border border-archival rounded-md overflow-hidden flex flex-col"
        >
          <div className="px-3 py-2 border-b border-archival bg-white flex items-center justify-between">
            <div className="text-[12px] text-archival-secondary font-medium truncate flex items-center gap-2">
              <span className="truncate">{candidate.attachment_filename}</span>
              {candidate.ocr_mode === "multi_page_pdf" && candidate.pdf_page_count > 0 && (
                <span
                  data-testid="review-pdf-page-count"
                  className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider bg-archival-soft border border-archival text-archival-muted"
                  title={
                    candidate.grouping_mode === "MANUAL"
                      ? `Grouped · ${candidate.pdf_page_count} pages · OCR processed ${candidate.pdf_pages_processed ?? 0}/${candidate.pdf_page_count}`
                      : `PDF · ${candidate.pdf_page_count} pages · OCR processed ${candidate.pdf_pages_processed ?? 0}/${candidate.pdf_page_count}`
                  }
                >
                  {candidate.grouping_mode === "MANUAL" ? "GROUP" : "PDF"} · {candidate.pdf_page_count}p · OCR {candidate.pdf_pages_processed ?? 0}/{candidate.pdf_page_count}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Viewer mode toggle */}
              <div
                className="inline-flex border border-archival rounded-md overflow-hidden mr-2"
                role="group"
                aria-label="Viewer mode"
              >
                <button
                  type="button"
                  data-testid="viewer-mode-fit"
                  onClick={() => { setViewMode("fit"); setZoom(1); }}
                  className={`px-2 py-1 text-[11px] font-medium transition-colors ${
                    viewMode === "fit"
                      ? "bg-archival-primary text-white"
                      : "bg-white text-archival-secondary hover:bg-archival-soft"
                  }`}
                  aria-pressed={viewMode === "fit"}
                >
                  Fit Bill
                </button>
                <button
                  type="button"
                  data-testid="viewer-mode-read"
                  onClick={() => { setViewMode("read"); setZoom(1); }}
                  className={`px-2 py-1 text-[11px] font-medium border-l border-archival transition-colors ${
                    viewMode === "read"
                      ? "bg-archival-primary text-white"
                      : "bg-white text-archival-secondary hover:bg-archival-soft"
                  }`}
                  aria-pressed={viewMode === "read"}
                >
                  Read Bill
                </button>
              </div>
              {!isReadOnly && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={TID.reviewExtractMissing}
                  className="h-7 mr-2 border-archival bg-white text-[11px] font-medium"
                  onClick={extractMissing}
                  disabled={extractingMissing}
                  title="Re-run OCR on the existing bill image and fill blank Qty / Unit ₹ / Tax / Total"
                >
                  {extractingMissing ? (
                    <Loader2 className="animate-spin mr-1" size={11} />
                  ) : (
                    <Wand2 size={11} className="mr-1" />
                  )}
                  Extract Missing Details
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))}
                aria-label="Zoom out"
                data-testid="viewer-zoom-out"
              >
                <ZoomOut size={14} />
              </Button>
              <span className="text-[11px] text-archival-muted font-mono w-10 text-center" data-testid="viewer-zoom-level">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
                aria-label="Zoom in"
                data-testid="viewer-zoom-in"
              >
                <ZoomIn size={14} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                aria-label="Rotate"
                data-testid="viewer-rotate"
              >
                <RotateCw size={14} />
              </Button>
              {/* Grouped-image navigation. Only rendered when the
                  candidate carries 2+ image attachments — PDFs use
                  the native iframe viewer's own page controls. */}
              {orderedPages.length >= 2 && candidate.attachment_content_type !== "application/pdf" && (
                <div className="ml-2 inline-flex items-center gap-1 border-l border-archival pl-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled={currentPageIdx <= 0}
                    onClick={() => goToPage(currentPageIdx - 1)}
                    aria-label="Previous page"
                    data-testid="viewer-prev-page"
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <span
                    data-testid="viewer-page-indicator"
                    className="text-[11px] text-archival-muted font-mono min-w-[64px] text-center"
                  >
                    Page {Math.max(1, currentPageIdx + 1)} of {orderedPages.length}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled={currentPageIdx >= orderedPages.length - 1}
                    onClick={() => goToPage(currentPageIdx + 1)}
                    aria-label="Next page"
                    data-testid="viewer-next-page"
                  >
                    <ChevronRight size={14} />
                  </Button>
                </div>
              )}
              {imgUrl && (
                <a
                  href={imgUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-archival-secondary hover:text-archival-primary p-1.5 inline-flex"
                  aria-label="Open original"
                >
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>

          {/* Source-page filmstrip for grouped candidates. Clickable
              in image mode so the operator can navigate between the
              underlying source files. PDFs continue to use the native
              iframe viewer which has its own page controls. */}
          {orderedPages.length >= 2 && (
            <div
              data-testid="review-page-strip"
              className="px-3 py-2 border-b border-archival bg-white flex items-center gap-1.5 overflow-x-auto"
            >
              {orderedPages.map((a, idx) => {
                const isActive = a.attachment_id === selectedAttachmentId;
                const isImage = candidate.attachment_content_type !== "application/pdf";
                return (
                  <button
                    key={a.attachment_id}
                    type="button"
                    disabled={!isImage}
                    onClick={() => goToPage(idx)}
                    data-testid={`review-page-strip-item-${a.page_number}`}
                    aria-current={isActive ? "page" : undefined}
                    title={a.filename}
                    className={
                      "inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-wider whitespace-nowrap transition-colors " +
                      (isActive
                        ? "bg-archival-primary text-white border-archival-primary"
                        : "bg-archival-soft border-archival text-archival-secondary hover:bg-white hover:border-archival-primary") +
                      (isImage ? " cursor-pointer" : " cursor-default opacity-60")
                    }
                  >
                    <span className={isActive ? "text-white" : "text-archival-primary"}>
                      Page {a.page_number}
                    </span>
                    <span className={isActive ? "text-white/70" : "text-archival-muted"}>·</span>
                    <span className="truncate max-w-[120px]">{a.filename}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div
            data-testid="viewer-pane"
            ref={paneRef}
            onScroll={() => { if (showScrollHint) setShowScrollHint(false); }}
            className="relative flex-1 overflow-auto p-4"
            style={{ display: "grid", placeItems: "safe center" }}
          >
            {imgUrl ? (
              candidate.attachment_content_type === "application/pdf" ? (
                <iframe
                  title="bill"
                  src={imgUrl}
                  className="w-full h-full bg-white rounded shadow-sm"
                />
              ) : (() => {
                // ----- Rotated-bounding-box viewer ------------------
                // Rotation with `transform: rotate()` around center
                // does NOT change the flow-layout box. When the
                // rotated image extends past its unrotated layout
                // box, part of it falls into negative scroll space
                // that native `overflow: auto` can't reach. Fix:
                // wrap the <img> in a <div> whose pixel dimensions
                // match the ROTATED bounding box; position the img
                // absolutely centered inside that wrapper. The pane
                // uses `place-items: safe center` so content is
                // centered when it fits and falls back to start-
                // alignment (positive overflow only) when it doesn't.
                const isRot90 = (rotation % 180) === 90;
                const nw = imgNat.w || 1;
                const nh = imgNat.h || 1;
                const padding = 32; // p-4 = 16px each side
                const paneInnerW = Math.max(1, paneSize.w - padding);
                const paneInnerH = Math.max(1, paneSize.h - padding);
                // Rotated dimensions (unscaled): (rotW, rotH)
                const rotW = isRot90 ? nh : nw;
                const rotH = isRot90 ? nw : nh;
                // Fit-scale so the rotated image fits inside the pane
                // at zoom=1 in "fit" mode. In "read" mode (or when
                // fit-by-height would push us above 100%) we anchor
                // fit to width — matches the previous behaviour of
                // `width: ${zoom*100}%` filling the pane width.
                const fitByW = paneInnerW / rotW;
                const fitByH = paneInnerH / rotH;
                const fitScale = viewMode === "fit"
                  ? Math.min(fitByW, fitByH, 1)
                  : fitByW;
                const s = fitScale * zoom;
                const imgPxW = nw * s;
                const imgPxH = nh * s;
                const wrapperPxW = isRot90 ? imgPxH : imgPxW;
                const wrapperPxH = isRot90 ? imgPxW : imgPxH;
                // Until natural dims are known (first render before
                // onLoad), let the browser lay out the image at its
                // natural size — the wrapper won't yet have the
                // correct rotated dims, but the pane centering keeps
                // it visible.
                const dimsKnown = imgNat.w > 0 && imgNat.h > 0 && paneSize.w > 0;
                return (
                  <div
                    style={{
                      position: "relative",
                      width: dimsKnown ? `${wrapperPxW}px` : "auto",
                      height: dimsKnown ? `${wrapperPxH}px` : "auto",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={imgUrl}
                      alt={candidate.attachment_filename}
                      data-testid="viewer-img"
                      onDoubleClick={() => {
                        setViewMode((m) => (m === "fit" ? "read" : "fit"));
                        setZoom(1);
                      }}
                      onLoad={(e) => {
                        const el = e.currentTarget;
                        if (el.naturalWidth && el.naturalHeight) {
                          setImgNat({ w: el.naturalWidth, h: el.naturalHeight });
                        }
                        const pane = paneRef.current;
                        if (!pane) return;
                        const overflows =
                          el.getBoundingClientRect().height >
                          pane.getBoundingClientRect().height + 4;
                        setShowScrollHint(viewMode === "read" && overflows);
                      }}
                      style={dimsKnown ? {
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: `${imgPxW}px`,
                        height: `${imgPxH}px`,
                        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                        transformOrigin: "center center",
                        transition: "width 150ms ease-out, height 150ms ease-out",
                        cursor: zoom > 1 ? "grab" : "zoom-in",
                        display: "block",
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      } : {
                        maxWidth: "100%",
                        maxHeight: "100%",
                        display: "block",
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      }}
                    />
                  </div>
                );
              })()
            ) : (
              <div className="text-archival-muted text-[12px]">Loading image…</div>
            )}

            {/* Scroll discovery hint — only when image overflows in Read mode */}
            {showScrollHint && (
              <div
                data-testid="viewer-scroll-hint"
                className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-archival-primary/90 text-white text-[11px] font-medium shadow-md animate-pulse"
              >
                ↓ Scroll to view remainder of bill
              </div>
            )}
          </div>
        </div>

        {/* Right column: review form */}
        <div className="bg-white border border-archival rounded-md flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
            <VendorPanel candidate={candidate} onChange={reload} />

            {/* Bill metadata */}
            <section className="border border-archival rounded-md bg-white">
              <header className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
                <div className="text-[13px] font-semibold text-archival-primary">Bill Metadata</div>
              </header>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted flex items-center gap-1">
                    Bill Number <OriginChip origin="OCR" />
                  </Label>
                  <Input
                    data-testid={TID.reviewBillNumber}
                    value={candidate.bill_number || ""}
                    onChange={(e) => update({ bill_number: e.target.value })}
                    disabled={isReadOnly}
                    className={`bg-white mt-1 font-mono ${
                      !isReadOnly && !(candidate.bill_number || "").trim()
                        ? "border-red-500"
                        : "border-archival"
                    }`}
                  />
                  {!isReadOnly && !(candidate.bill_number || "").trim() && (
                    <p
                      data-testid="review-bill-number-error"
                      className="mt-1 text-[11px] text-red-700"
                    >
                      Bill number is required before sending to EC.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Bill Date</Label>
                  <Input
                    data-testid={TID.reviewBillDate}
                    type="date"
                    value={candidate.bill_date || ""}
                    onChange={(e) => update({ bill_date: e.target.value })}
                    disabled={isReadOnly}
                    className={`bg-white mt-1 font-mono ${
                      !isReadOnly && !(candidate.bill_date || "").trim()
                        ? "border-red-500"
                        : "border-archival"
                    }`}
                  />
                  {!isReadOnly && !(candidate.bill_date || "").trim() && (
                    <p
                      data-testid="review-bill-date-error"
                      className="mt-1 text-[11px] text-red-700"
                    >
                      Bill date is required before sending to EC.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Currency</Label>
                  <Input
                    value={candidate.currency || "INR"}
                    onChange={(e) => update({ currency: e.target.value })}
                    disabled={isReadOnly}
                    className="bg-white border-archival mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Bill Total *</Label>
                  <Input
                    data-testid={TID.reviewTotalAmount}
                    type="number"
                    value={candidate.total_amount ?? ""}
                    onChange={(e) =>
                      update({
                        total_amount: e.target.value === "" ? null : Number(e.target.value),
                        total_amount_origin: "EDITED",
                      })
                    }
                    disabled={isReadOnly}
                    className="bg-white border-archival mt-1 font-mono font-semibold"
                  />
                </div>
              </div>
            </section>

            {/* Line items */}
            <section className="border border-archival rounded-md bg-white">
              {needsExtractMissing && (
                <div
                  data-testid={TID.reviewExtractMissingBanner}
                  className="flex items-start gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 rounded-t-md"
                >
                  <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                  <div className="flex-1 text-[12px] text-amber-900 leading-snug">
                    <strong className="font-semibold">OCR extraction incomplete.</strong>{" "}
                    Some quantities, prices, taxes, or totals are missing.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 border-amber-300 bg-white text-amber-900 text-[11px] font-medium hover:bg-amber-100"
                    onClick={extractMissing}
                    disabled={extractingMissing}
                    data-testid={`${TID.reviewExtractMissing}-banner`}
                  >
                    {extractingMissing ? (
                      <Loader2 className="animate-spin mr-1" size={11} />
                    ) : (
                      <Wand2 size={11} className="mr-1" />
                    )}
                    Extract Missing Details
                  </Button>
                </div>
              )}
              <header className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
                <div className="text-[13px] font-semibold text-archival-primary">Line Items</div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={TID.reviewAddLine}
                  className="border-archival bg-white"
                  disabled={isReadOnly}
                  onClick={() =>
                    update({
                      line_items: [
                        ...(candidate.line_items || []),
                        { name: "", quantity: null, unit: "", unit_price: null, tax_pct: null, line_total: null },
                      ],
                    })
                  }
                >
                  <Plus size={12} /> Add line
                </Button>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-archival-soft text-archival-muted">
                    <tr className="text-left">
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider">Item</th>
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider w-28">Qty</th>
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider w-20">Unit</th>
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider w-24">Unit ₹</th>
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider w-20">Tax%</th>
                      <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider w-24">Total</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(candidate.line_items || []).map((li, i) => (
                      <tr
                        key={i}
                        data-testid={TID.reviewLineRow(i)}
                        className="border-t border-archival"
                      >
                        <td className="px-2 py-1">
                          <Input
                            data-testid={TID.reviewLineName(i)}
                            value={li.name || ""}
                            disabled={isReadOnly}
                            onChange={(e) => {
                              const items = [...candidate.line_items];
                              items[i] = { ...items[i], name: e.target.value };
                              update({ line_items: items });
                            }}
                            className="border-archival h-8"
                          />
                          {li.page_number != null && candidate.ocr_mode === "multi_page_pdf" && (
                            <span
                              data-testid={`review-line-page-${i}`}
                              className="inline-flex mt-0.5 items-center px-1 rounded-sm text-[9px] font-mono uppercase tracking-wider text-archival-muted"
                              title={`Extracted from page ${li.page_number}`}
                            >
                              Page {li.page_number}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            data-testid={TID.reviewLineQty(i)}
                            type="number"
                            step="any"
                            value={li.quantity ?? ""}
                            disabled={isReadOnly}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => updateLineItem(i, "quantity", e.target.value)}
                            className="border-archival h-8 font-mono numeric-input"
                          />
                          {li.quantity_origin === "DERIVED" && (
                            <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid={`review-line-qty-origin-${i}`}>derived</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <UnitCombobox
                            testid={TID.reviewLineUnit(i)}
                            value={li.unit || ""}
                            disabled={isReadOnly}
                            units={units}
                            recentUnits={recentUnits}
                            degraded={unitsDegraded}
                            onChange={(v) => {
                              const items = [...candidate.line_items];
                              items[i] = { ...items[i], unit: v };
                              update({ line_items: items });
                            }}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            data-testid={TID.reviewLineUnitPrice(i)}
                            type="number"
                            step="any"
                            value={li.unit_price ?? ""}
                            disabled={isReadOnly}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => updateLineItem(i, "unit_price", e.target.value)}
                            className="border-archival h-8 font-mono numeric-input"
                          />
                          {li.unit_price_origin === "DERIVED" && (
                            <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid={`review-line-unit-price-origin-${i}`}>derived</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            data-testid={TID.reviewLineTaxPct(i)}
                            type="number"
                            step="any"
                            value={li.tax_pct ?? li.tax_percent ?? ""}
                            disabled={isReadOnly}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => updateLineItem(i, "tax_pct", e.target.value)}
                            className="border-archival h-8 font-mono numeric-input"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            data-testid={TID.reviewLineTotal(i)}
                            type="number"
                            step="any"
                            value={li.line_total ?? ""}
                            disabled={isReadOnly}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => updateLineItem(i, "line_total", e.target.value)}
                            className="border-archival h-8 font-mono font-semibold numeric-input"
                          />
                          {li.line_total_origin === "DERIVED" && (
                            <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid={`review-line-total-origin-${i}`}>derived</span>
                          )}
                          {li.line_total_origin === "EDITED" && (
                            <span
                              className="mt-0.5 flex items-center gap-1.5 text-[10px] text-amber-800"
                              data-testid={`review-line-total-manual-${i}`}
                            >
                              <span className="whitespace-nowrap">Line total was manually edited</span>
                              <button
                                type="button"
                                disabled={
                                  isReadOnly ||
                                  !isFiniteNumeric(li.quantity) ||
                                  !isFiniteNumeric(li.unit_price)
                                }
                                onClick={() => recomputeLineTotal(i)}
                                data-testid={`review-line-total-recompute-${i}`}
                                className="underline font-medium text-amber-900 hover:text-archival-primary disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                              >
                                Recompute
                              </button>
                            </span>
                          )}
                        </td>
                        <td className="px-1 py-1 text-center">
                          <button
                            data-testid={TID.reviewLineRemove(i)}
                            disabled={isReadOnly}
                            onClick={() => {
                              const items = candidate.line_items.filter((_, j) => j !== i);
                              update({ line_items: items });
                            }}
                            className="text-archival-muted hover:text-red-700 disabled:opacity-30"
                            aria-label="Remove line"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!candidate.line_items || candidate.line_items.length === 0) && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-archival-muted text-[12px]">
                          No line items yet. Click <em>Add line</em> to start.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Totals / reconciliation */}
            <section className="border border-archival rounded-md bg-white">
              <header className="px-4 py-2.5 border-b border-archival flex items-center justify-between">
                <div className="text-[13px] font-semibold text-archival-primary">Totals & Reconciliation</div>
                <StatusChip status={(linesDirtyRef.current || !candidate.reconciliation_state)
                  ? (totalMismatch == null ? candidate.reconciliation_state
                     : Math.abs(totalMismatch) <= 1 ? "EXACT_MATCH" : "MISMATCH")
                  : candidate.reconciliation_state} />
              </header>
              <div className="p-4 grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Subtotal</Label>
                  <Input
                    data-testid={TID.reviewSubtotal}
                    type="number"
                    value={candidate.subtotal ?? ""}
                    disabled={isReadOnly}
                    onChange={(e) =>
                      update({
                        subtotal: e.target.value === "" ? null : Number(e.target.value),
                        subtotal_origin: "EDITED",
                      })
                    }
                    className="bg-white border-archival mt-1 font-mono"
                  />
                  {candidate.subtotal_origin === "DERIVED" && (
                    <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid="review-subtotal-origin">derived</span>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Tax Total</Label>
                  <Input
                    data-testid={TID.reviewTaxTotal}
                    type="number"
                    value={candidate.tax_total ?? ""}
                    disabled={isReadOnly}
                    onChange={(e) =>
                      update({
                        tax_total: e.target.value === "" ? null : Number(e.target.value),
                        tax_total_origin: "EDITED",
                      })
                    }
                    className="bg-white border-archival mt-1 font-mono"
                  />
                  {candidate.tax_total_origin === "DERIVED" && (
                    <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid="review-tax-total-origin">derived</span>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-archival-muted">Bill Total</Label>
                  <div className="mt-1 px-3 py-2 rounded-md border border-archival bg-archival-soft font-mono text-[14px] font-semibold">
                    {formatAmount(candidate.total_amount, candidate.currency)}
                  </div>
                  {candidate.total_amount_origin === "EDITED" && (
                    <span className="block mt-0.5 text-[9px] uppercase tracking-wider text-archival-muted" data-testid="review-total-origin">corrected by you</span>
                  )}
                </div>
              </div>

              {/* Printed financial components — only the rows this document actually prints. */}
              {charges.hasRows && (
                <div className="px-4 pb-3" data-testid="review-components">
                  <div className="text-[11px] uppercase tracking-wider text-archival-muted mb-1.5">
                    Printed on the bill
                  </div>
                  <div className="space-y-1.5">
                    {charges.rows.map((row) => (
                      <div
                        key={row.index}
                        className="flex items-center gap-3"
                        data-testid={`review-component-${row.kind.toLowerCase()}`}
                      >
                        <div className="w-44 shrink-0 text-[12px] text-archival-primary">
                          <span className="font-medium">{COMPONENT_LABEL[row.kind] || row.kind}</span>
                          <span className="block text-[10px] text-archival-muted truncate">{row.label}</span>
                        </div>
                        <Input
                          data-testid={`review-component-amount-${row.index}`}
                          type="number"
                          value={row.amount ?? ""}
                          disabled={isReadOnly}
                          onChange={(e) => updateCharge(row.index, e.target.value)}
                          className="bg-white border-archival font-mono h-8 max-w-[160px]"
                        />
                        {row.edited && row.ocrAmount != null && (
                          <span
                            className="text-[10px] text-archival-muted"
                            data-testid={`review-component-ocr-${row.index}`}
                          >
                            OCR read {formatAmount(row.ocrAmount, candidate.currency)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-center justify-between text-[12px] border-t border-archival pt-2">
                    <span className="text-archival-muted">
                      Taxable {formatAmount(computedFooter.subtotal, candidate.currency)}
                      {computedFooter.lines_tax_inclusive ? " · printed line amounts include tax" : ""}
                    </span>
                    <span className="font-mono font-semibold" data-testid="review-computed-total">
                      Computed total {formatAmount(computedFooter.total_amount, candidate.currency)}
                    </span>
                  </div>
                </div>
              )}

              {/* Every printed part agrees with the others; only the grand total does not. */}
              {candidate.total_amount_conflict && (
                <div className="px-4 pb-3 text-[12px] text-amber-800" data-testid="review-total-conflict">
                  The printed parts of this bill add up to{" "}
                  <span className="font-mono">
                    {formatAmount(candidate.total_amount_conflict.reconciled_value, candidate.currency)}
                  </span>
                  , but the total was read as{" "}
                  <span className="font-mono">
                    {formatAmount(candidate.total_amount_conflict.ocr_value, candidate.currency)}
                  </span>
                  . Check it against the bill image and correct it above.
                </div>
              )}
              {candidate.reconciliation_variance != null && Math.abs(candidate.reconciliation_variance) > 0.01 && (
                <div className="px-4 pb-3 text-[12px] text-amber-800">
                  Line item total differs by {formatAmount(candidate.reconciliation_variance, candidate.currency)}.
                </div>
              )}
              {/* OCR-vs-computed bill totals mismatch. Non-blocking; save
                  is still allowed. Send-to-EC is separately gated by the
                  backend pre-flight guard when the delta exceeds ₹1. */}
              {totalMismatch != null && Math.abs(totalMismatch) > 1 && (
                <div
                  data-testid="review-totals-mismatch-banner"
                  className="px-4 pb-3 text-[12px] text-red-700"
                >
                  <div>
                    OCR total: {formatAmount(candidate.total_amount, candidate.currency)}
                    {"  ·  "}
                    Computed total: {formatAmount(computedFooter.total_amount, candidate.currency)}
                    {"  ·  "}
                    Δ {formatAmount(totalMismatch, candidate.currency)}
                  </div>
                  {Math.abs(totalMismatch) > 1 && (
                    <div className="mt-1 text-[11px]">
                      Confirming in 1OS Bills will be blocked until totals match within ₹1.
                    </div>
                  )}
                </div>
              )}
              {(candidate.warning_reasons?.length || candidate.blocker_reasons?.length) ? (
                <div className="px-4 pb-3 space-y-1">
                  {(candidate.blocker_reasons || []).map((b, i) => (
                    <div key={`b${i}`} className="text-[12px] text-red-700 flex items-start gap-1">
                      <XCircle size={12} className="mt-0.5" /> {b}
                    </div>
                  ))}
                  {(candidate.warning_reasons || []).map((w, i) => (
                    <div key={`w${i}`} className="text-[12px] text-amber-800 flex items-start gap-1">
                      <AlertTriangle size={12} className="mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              ) : null}
            </section>

            {/* Duplicate */}
            {candidate.duplicate_state && candidate.duplicate_state !== "NO_DUPLICATE" && candidate.duplicate_state !== "NOT_CHECKED" && (
              <section
                data-testid="candidate-duplicate-panel"
                className={`border rounded-md p-4 ${
                  candidate.duplicate_state === "HARD_DUPLICATE"
                    ? "border-red-200 bg-red-50"
                    : "border-amber-200 bg-amber-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-red-900">
                      {candidate.duplicate_state === "HARD_DUPLICATE"
                        ? "Duplicate bill detected"
                        : "Possible duplicate"}
                    </div>
                    {candidate.duplicate_existing_bill ? (
                      <div className="text-[12px] mt-1 text-red-900">
                        A bill already exists
                        {" "}<span className="font-semibold">#{candidate.duplicate_existing_bill.bill_number || "—"}</span>
                        {candidate.duplicate_existing_bill.bill_date ? <> dated <span className="font-semibold">{candidate.duplicate_existing_bill.bill_date}</span></> : null}
                        {candidate.duplicate_existing_bill.vendor_name ? <> for <span className="font-semibold">{candidate.duplicate_existing_bill.vendor_name}</span></> : null}
                        .
                      </div>
                    ) : candidate.duplicate_message ? (
                      <div className="text-[12px] mt-1 text-red-900">{asText(candidate.duplicate_message)}</div>
                    ) : (
                      <div className="text-[12px] mt-1 text-red-900">
                        Matched bill ID:{" "}
                        <span className="font-mono">
                          {candidate.duplicate_candidate_bill_id?.slice(0, 8) || "—"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {candidate.duplicate_existing_bill?.url && (
                      <a
                        href={candidate.duplicate_existing_bill.url}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="duplicate-open-existing"
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-red-800 hover:text-red-900 underline"
                      >
                        Open existing bill ↗
                      </a>
                    )}
                    {candidate.status !== "REJECTED"
                      && candidate.status !== "SENT_TO_EC"
                      && candidate.status !== "CONFIRMED_LINKED" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="duplicate-reject-candidate"
                        onClick={() => setRejectOpen(true)}
                        className="border-red-300 bg-white text-red-800 hover:bg-red-100"
                      >
                        Reject this candidate
                      </Button>
                    )}
                  </div>
                </div>
              </section>
            )}

            {candidate.status === "OCR_FAILED" && (
              <Button
                onClick={retryOcr}
                data-testid={TID.reviewRetryOcr}
                variant="outline"
                className="border-archival bg-white"
              >
                <RotateCw size={14} /> Retry OCR
              </Button>
            )}
          </div>

          {/* Sticky action bar */}
          <div
            className="border-t border-archival bg-white px-4 py-3 flex items-center justify-between gap-3"
            style={{ boxShadow: "0 -4px 12px rgba(0,0,0,0.03)" }}
          >
            <div className="text-[12px] text-archival-muted">
              {confirmDisabledReason ? (
                <span className="text-amber-800">{confirmDisabledReason}</span>
              ) : (
                "All checks passed — ready to send to EC."
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isReadOnly && billsOwned && (
                <>
                  <span className="text-[12px] text-archival-secondary" data-testid="bills-owned-note">Being reviewed in Bills</span>
                  <Button data-testid="open-bill-review-btn" onClick={() => navigate(`/bills/${candidateId}`)}
                    className="text-white" style={{ backgroundColor: "var(--action)" }}>Open bill review</Button>
                </>
              )}
              {!isReadOnly && !billsOwned && nativeAuthority === "UNCLAIMED" && (
                <Button variant="outline" data-testid="start-bill-review-link" onClick={() => navigate(`/bills/${candidateId}`)}
                  className="border-archival bg-white">Review in Bills</Button>
              )}
              {!isReadOnly && !billsOwned && (
                <>
                  <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid={TID.reviewReject} className="border-archival bg-white">
                        Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogHeader>
                        <DialogTitle>Reject candidate</DialogTitle>
                      </DialogHeader>
                      <Textarea
                        data-testid={TID.reviewRejectReason}
                        placeholder="Reason (optional)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={3}
                        className="bg-white border-archival"
                      />
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
                        <Button
                          data-testid={TID.reviewRejectSubmit}
                          onClick={reject}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Reject candidate
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="outline"
                    data-testid={TID.reviewSave}
                    onClick={save}
                    disabled={saving}
                    className="border-archival bg-white"
                  >
                    {saving ? <Loader2 className="animate-spin mr-1" size={12} /> : null}
                    Save Review
                  </Button>
                  <Button
                    data-testid={TID.reviewSendToEc}
                    onClick={() => navigate(`/bills/${candidate.id}`)}
                    disabled={
                      !!confirmDisabledReason || !canConfirm ||
                      (totalMismatch != null && Math.abs(totalMismatch) > 1)
                    }
                    title={
                      !canConfirm
                        ? "Missing BULK_CAPTURE_CONFIRM capability"
                        : (!candidate.bill_number?.trim() || !candidate.bill_date?.trim())
                        ? "Fill bill number and bill date before continuing in 1OS Bills."
                        : (totalMismatch != null && Math.abs(totalMismatch) > 1)
                        ? `Bill totals mismatch by ${formatAmount(totalMismatch, candidate.currency)}. Fix line items or bill total to within ₹1 before continuing.`
                        : confirmDisabledReason || undefined
                    }
                    className="text-white"
                    style={{ backgroundColor: "var(--action)" }}
                  >
                    Continue in 1OS Bills
                  </Button>
                </>
              )}
              {isReadOnly && (
                <>
                  {candidate.ec_bill_url && (
                    <a
                      href={candidate.ec_bill_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 rounded-md border border-archival bg-white hover:bg-archival-soft"
                    >
                      Open historical EC record <ExternalLink size={12} />
                    </a>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => navigate("/queue")}
                    className="border-archival bg-white"
                  >
                    Back to queue
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
